/**
 * Which challenge the player is in the middle of, so a refresh can find the round again.
 *
 * A ranked round lives on the server and a reload loses everything the page held: the session id, its token, the
 * board. What survives is the browser tab's own `sessionStorage`, which is exactly the right lifetime: it outlives
 * a reload and not the tab, so a round the player walked away from days ago is never "resumed" into. Only the
 * challenge's id is kept, never a token or a card: `GET /api/challenges/:id/session` (cookie auth) hands back the
 * rest, and only for the account that owns the round.
 *
 * Set when a ranked round starts, cleared when it ends. Storage can be missing or throw (a private window, blocked
 * site data); everything here swallows that, and a round simply cannot be resumed there.
 *
 * Two tabs (a duplicated tab copies `sessionStorage`) are not coordinated here. The server decides who is driving
 * a round: the one that claimed it last (deal.ts `checkDriver`), and the other stands down.
 */

const KEY = 'w21.liveRound';

/** Remember the challenge whose round has just started. */
export function rememberLiveRound(challengeId: string): void {
  try {
    globalThis.sessionStorage?.setItem(KEY, challengeId);
  } catch {
    /* no storage: no resume */
  }
}

/** The challenge a round was in progress on when the page last unloaded, or null. */
export function recallLiveRound(): string | null {
  try {
    const id = globalThis.sessionStorage?.getItem(KEY) ?? null;
    return id !== null && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

/** The round has ended (or cannot be reached): there is nothing to come back to. */
export function forgetLiveRound(): void {
  try {
    globalThis.sessionStorage?.removeItem(KEY);
  } catch {
    /* nothing to forget */
  }
}
